import { Col, Row } from "antd";
import cuid from "cuid";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCallReceived,
  MdOutlineQrCode,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { PAYMENT_RECEIVER, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Refund = ({
  setDisabled,
  setPercent,
  bindField,
  initialValues,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const hasPayerBankDetails =
    get(initialValues, "payment.payer.IBAN") &&
    get(initialValues, "payment.payer.BIC");

  const PAYMENT_RECEIVER_VALUES = {
    [PAYMENT_RECEIVER.SAME_ACCOUNT]: () =>
      set(
        initialValues,
        "payment.receiver",
        get(initialValues, "payment.payer"),
      ),
    [PAYMENT_RECEIVER.ANOTHER_ACCOUNT]: () =>
      set(initialValues, "payment.receiver", {
        id: cuid(),
        owner: { id: cuid() },
      }),
  };

  const isPayerReceiverPath = "isPayerReceiver";
  const isPayerReceiverField = bindField(isPayerReceiverPath, {
    label: SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT.LABEL,
    required: isFieldRequired(isPayerReceiverPath),
    disabled: !hasPayerBankDetails,
    onChange: (value) => {
      setDisabled((prev) => ({
        ...prev,
        proof: {
          ...prev.proof,
          refundAccount: value === PAYMENT_RECEIVER.SAME_ACCOUNT,
        },
      }));
      PAYMENT_RECEIVER_VALUES[value]();
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "payment.receiver.owner.lastName";
  const lastNameField = bindField(lastNamePath, {
    label: SUBSCRIPTION.FORM.OWNER.LASTNAME.LABEL,
    required: isFieldRequired(lastNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstNamePath = "payment.receiver.owner.firstName";
  const firstNameField = bindField(firstNamePath, {
    label: SUBSCRIPTION.FORM.OWNER.FIRSTNAME.LABEL,
    required: isFieldRequired(firstNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const ibanPath = "payment.receiver.IBAN";
  const ibanField = bindField(ibanPath, {
    label: SUBSCRIPTION.FORM.IBAN.LABEL,
    required: isFieldRequired(ibanPath),
    onBlur: () => setPercent(initialValues),
  });

  const bicPath = "payment.receiver.BIC";
  const bicField = bindField(bicPath, {
    label: SUBSCRIPTION.FORM.BIC.LABEL,
    required: isFieldRequired(bicPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={isPayerReceiverField.path}
              label={isPayerReceiverField.label}
              required={isPayerReceiverField.required}
              disabled={isPayerReceiverField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdOutlineCallReceived size={16} />}
                  label={isPayerReceiverField.label}
                  value={get(initialValues, isPayerReceiverPath)}
                  values={[
                    {
                      value: PAYMENT_RECEIVER.SAME_ACCOUNT,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .OPTIONS.USER_ACCOUNT,
                      popover:
                        !hasPayerBankDetails &&
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .POPOVER,
                    },
                    {
                      value: PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
                      label:
                        SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                          .OPTIONS.OTHER,
                    },
                  ]}
                  {...field}
                  onBlur={isPayerReceiverField.onBlur}
                  onChange={isPayerReceiverField.onChange}
                />
              )}
            </FormField>
          </Col>
          {get(initialValues, "isPayerReceiver") ===
            PAYMENT_RECEIVER.ANOTHER_ACCOUNT && (
            <>
              <Col lg={12} xs={12}>
                <FormField
                  path={lastNameField.path}
                  label={lastNameField.label}
                  required={lastNameField.required}
                  disabled={lastNameField.disabled}
                >
                  {(field) => (
                    <Input
                      label={lastNameField.label}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={SUBSCRIPTION.FORM.OWNER.LASTNAME.PLACEHOLDER}
                      type="custom"
                      normalizeText={true}
                      defaultValue={lastNameField.defaultValue}
                      {...field}
                      onChange={lastNameField.onChange}
                      onBlur={lastNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={12} xs={12}>
                <FormField
                  path={firstNameField.path}
                  label={firstNameField.label}
                  required={firstNameField.required}
                  disabled={firstNameField.disabled}
                >
                  {(field) => (
                    <Input
                      label={firstNameField.label}
                      icon={<MdOutlineBadge size={16} />}
                      placeholder={
                        SUBSCRIPTION.FORM.OWNER.FIRSTNAME.PLACEHOLDER
                      }
                      type="custom"
                      normalizeText={true}
                      defaultValue={firstNameField.defaultValue}
                      {...field}
                      onChange={firstNameField.onChange}
                      onBlur={firstNameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={ibanField.path}
                  label={ibanField.label}
                  required={ibanField.required}
                  disabled={ibanField.disabled}
                >
                  {(field) => (
                    <Input
                      label={ibanField.label}
                      icon={<MdOutlineQrCode size={16} />}
                      placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                      type="custom"
                      trim={true}
                      defaultValue={ibanField.defaultValue}
                      {...field}
                      onChange={ibanField.onChange}
                      onBlur={ibanField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={bicField.path}
                  label={bicField.label}
                  required={bicField.required}
                  disabled={bicField.disabled}
                >
                  {(field) => (
                    <Input
                      label={bicField.label}
                      icon={<MdOutlineQrCode size={16} />}
                      placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                      type="custom"
                      trim={true}
                      defaultValue={bicField.defaultValue}
                      {...field}
                      onChange={bicField.onChange}
                      onBlur={bicField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Refund;
